import { ChevronLeft, ChevronRight } from '@atom-learning/icons'
import { List } from '@radix-ui/react-tabs'
import React from 'react'

import {
  ColorScheme,
  TcolorScheme
} from '~/experiments/color-scheme/ColorScheme'
import { styled } from '~/styled'
import { useCallbackRefState } from '~/utilities/hooks/useCallbackRef'
import { useScrollPosition } from '~/utilities/hooks/useScrollPosition'
import { useSize } from '~/utilities/hooks/useSize'

import { ActionIcon } from '../action-icon/ActionIcon'
import { Icon } from '../icon/Icon'

const StyledContainer = styled(ColorScheme, {
  base: ['relative', 'border-b', 'border-b-(--base-4)', 'w-full']
})

const StyledTriggerList = styled(List, {
  base: [
    'shrink-0',
    'flex',
    'w-full',
    'overflow-x-auto',
    '-webkit-scrollbar:hidden',
    '[scrollbar-width:none]'
  ]
})

const StyledChevronActionIcon = styled(ActionIcon, {
  base: [
    'h-full!',
    'absolute',
    'top-1/2',
    '-translate-y-1/2',
    'cursor-pointer',
    'bg-(--base-1)!',
    'text-(--interactive-1)!',
    'rounded-none',
    'opacity-90'
  ]
})

const SCROLL_STEP = 0.8 // Used to scroll 80% of clientWidth

export const TabsTriggerList = ({
  children,
  colorScheme = {},
  ...rest
}: React.ComponentProps<typeof StyledTriggerList> & {
  colorScheme?: TcolorScheme
}) => {
  const [listRef, setListRefCallback] = useCallbackRefState()

  const { width } = useSize({ element: listRef, delay: 500 })
  const { left } = useScrollPosition({
    element: listRef,
    delay: 100,
    delayMethod: 'debounce'
  })

  const canScrollXAxis = React.useMemo(() => {
    if (!listRef) return false
    return listRef.scrollWidth > listRef.clientWidth
  }, [width, listRef])

  const canScrollLeft = React.useMemo(() => {
    if (!canScrollXAxis) return false
    return left > 0
  }, [left, canScrollXAxis])

  const canScrollRight = React.useMemo(() => {
    if (!listRef || !canScrollXAxis) return false
    return listRef.scrollWidth - left - listRef.clientWidth > 1 // 1 rather than 0 to account for sub-pixel widths and calculations
  }, [listRef, left, canScrollXAxis])

  const scrollList = React.useCallback(
    (stepModifier) => {
      if (!listRef) return
      listRef.scroll({
        left: listRef.scrollLeft + listRef.clientWidth * stepModifier,
        behavior: 'smooth'
      })
    },
    [listRef]
  )

  return (
    <StyledContainer
      base="grey1"
      accent="primary1"
      interactive="hiContrast"
      {...colorScheme}
      {...rest}
    >
      <StyledTriggerList ref={setListRefCallback}>{children}</StyledTriggerList>

      {canScrollLeft && (
        <StyledChevronActionIcon
          label="scroll left"
          size="md"
          onClick={() => scrollList(-SCROLL_STEP)}
          tabIndex={-1}
          className="left-0"
        >
          <Icon is={ChevronLeft} />
        </StyledChevronActionIcon>
      )}

      {canScrollRight && (
        <StyledChevronActionIcon
          label="scroll right"
          size="md"
          onClick={() => scrollList(SCROLL_STEP)}
          tabIndex={-1}
          className="right-0"
        >
          <Icon is={ChevronRight} />
        </StyledChevronActionIcon>
      )}
    </StyledContainer>
  )
}

TabsTriggerList.displayName = 'TabsTriggerList'
